import { useRef } from 'react';
import { Modal } from '@/shared/ui/Modal/Modal';
import { Button } from '@/shared/ui/Button/Button';
import { useRestoreUserMutation } from '@/entities/User';
import { useRestoreClubMutation } from '@/entities/Club';
import { useError } from '@/shared/lib/hooks/useError';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import s from '@/features/DeleteAccount/ui/DeleteAccountModal/DeleteAccountModal.module.scss';

interface RestoreAccountModalProps {
  open: boolean;
  userId: number;
  userName: string;
  mode?: 'account' | 'club';
  onClose: () => void;
}

export const RestoreAccountModal = ({
  open,
  userId,
  userName,
  mode = 'account',
  onClose,
}: RestoreAccountModalProps) => {
  const onAlert = useAlert();
  const { isMobile } = useScreenDetector();
  const [restoreUser, userState] = useRestoreUserMutation();
  const [restoreClub, clubState] = useRestoreClubMutation();
  const isClub = mode === 'club';
  const { error, isLoading } = isClub ? clubState : userState;
  const isSubmittingRef = useRef(false);

  const handleRestore = async () => {
    if (isLoading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      await (isClub ? restoreClub(userId) : restoreUser(userId)).unwrap();
      onAlert({
        header: isClub ? 'Клуб' : 'Учётная запись',
        body: [userName],
        footer: isClub ? 'успешно восстановлен' : 'успешно восстановлена',
      });
      onClose();
    } catch {
      return;
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useError(error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isClub ? 'Восстановление клуба' : 'Восстановление учётной записи'}
      titleSize="h1"
    >
      <div className={s.wrapper}>
        Вы действительно хотите восстановить {isClub ? 'клуб' : 'учётную запись'} <strong>{userName || '-'}</strong>?
      </div>
      <div className={s.actions}>
        <Button onClick={() => void handleRestore()} disabled={isLoading} size={isMobile ? 's' : 'm'}>
          Восстановить
        </Button>
        <Button color="red" variant="outline" onClick={onClose} disabled={isLoading} size={isMobile ? 's' : 'm'}>
          Отменить
        </Button>
      </div>
    </Modal>
  );
};
